<template>
  <div class="sms-log-page">
    <md-card>
      <md-card-header class="md-card-header-icon md-card-header-primary">
        <div class="card-icon">
          <md-icon>sms</md-icon>
        </div>
        <h4 class="title">SMS Logs</h4>
        <p class="category">View all SMS messages sent from the system</p>
      </md-card-header>

      <md-card-content>
        <!-- Stats Cards -->
        <div class="stats-cards md-layout md-gutter" v-if="stats">
          <div class="md-layout-item md-size-20" @click="setFilter('statusCode', '48200')">
            <div class="stat-card success">
              <div class="stat-value">{{ formatNumber(stats.successful || 0) }}</div>
              <div class="stat-label">Successful</div>
            </div>
          </div>
          <div class="md-layout-item md-size-20" @click="setFilter('statusCode', '0')">
            <div class="stat-card danger">
              <div class="stat-value">{{ formatNumber(stats.failed || 0) }}</div>
              <div class="stat-label">Failed</div>
            </div>
          </div>
          <div class="md-layout-item md-size-20" @click="setFilter('statusCode', 'pending')">
            <div class="stat-card warning">
              <div class="stat-value">{{ formatNumber(stats.pending || 0) }}</div>
              <div class="stat-label">Pending</div>
            </div>
          </div>
          <div class="md-layout-item md-size-20">
            <div class="stat-card info">
              <div class="stat-value">{{ formatNumber(stats.total || 0) }}</div>
              <div class="stat-label">Total Messages</div>
            </div>
          </div>
          <div class="md-layout-item md-size-20">
            <div class="stat-card primary">
              <div class="stat-value">{{ formatNumber(stats.balance || 0) }}</div>
              <div class="stat-label">Current Balance</div>
            </div>
          </div>
        </div>

        <!-- Filters -->
        <div class="filters-bar md-layout md-gutter">
          <div class="md-layout-item md-size-16">
            <md-field>
              <label>From Date</label>
              <md-input type="date" v-model="filters.from_date" @change="onFilterChange" />
            </md-field>
          </div>
          <div class="md-layout-item md-size-16">
            <md-field>
              <label>To Date</label>
              <md-input type="date" v-model="filters.to_date" @change="onFilterChange" />
            </md-field>
          </div>
          <div class="md-layout-item md-size-16">
            <md-field>
              <label>Status</label>
              <md-select v-model="filters.statusCode" @md-selected="onFilterChange">
                <md-option value="">All Status</md-option>
                <md-option value="100">Successful</md-option>
                <md-option value="0">Failed</md-option>
                <md-option value="pending">Pending</md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-size-16">
            <md-field>
              <label>Subject Type</label>
              <md-select v-model="filters.subject_type" @md-selected="onFilterChange">
                <md-option value="">All Types</md-option>
                <md-option value="guidance">Guidance</md-option>
                <md-option value="schedule">Schedule</md-option>
                <md-option value="labourer">Labourer</md-option>
                <md-option value="takeon">Takeon</md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-size-20">
            <md-field>
              <label>Per Page</label>
              <md-select v-model="pagination.perPage" @md-selected="onPerPageChange">
                <md-option v-for="item in perPageOptions" :key="item" :value="item">
                  {{ item }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-size-16">
            <md-field>
              <label>Search</label>
              <md-input v-model="filters.search" @input="onSearchChange" placeholder="Search by content, recipient..." />
            </md-field>
          </div>
        </div>

        <!-- Bulk Actions -->
        <div class="bulk-actions" v-if="selectedLogs && selectedLogs.length > 0">
          <span class="selected-count">{{ selectedLogs.length }} record(s) selected</span>
          <md-button class="md-primary md-raised" @click="resendSelected">
            <md-icon>send</md-icon> Resend Selected
          </md-button>
          <md-button class="md-danger md-raised" @click="clearSelection">
            Clear Selection
          </md-button>
        </div>

        <!-- Loading -->
        <div v-if="isPageLoading" class="loading-container">
          <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
        </div>

        <!-- SMS Log Table -->
        <div class="sms-log-table" v-else>
          <md-table v-if="logsData && logsData.length">
            <md-table-row v-for="(item,ind) in logsData" :key="item.id+'-'+ind">
              <md-table-cell md-label="">
                <md-checkbox v-model="selectedLogs" :value="item" />
              </md-table-cell>
              <md-table-cell md-label="Recipient">
                <strong>{{ item.recipient }}</strong>
                <br>
                <span class="receiver-info" v-if="item.receiver">
                  <span class="receiver-type-badge" :class="getReceiverTypeClass(item.receiver_type)">
                    {{ formatReceiverType(item.receiver_type) }}
                  </span>
                  {{ getReceiverName(item.receiver) }}
                </span>
              </md-table-cell>
              <md-table-cell md-label="Content">
                <div class="content-preview">
                  {{ truncateText(item.content, 100) }}
                </div>
                <div class="content-full" v-if="item.content && item.content.length > 100">
                  <small>
                    <a href="#" @click.prevent="showFullContent(item)">Read more</a>
                  </small>
                </div>
              </md-table-cell>
              <md-table-cell md-label="Subject">

                    {{ formatSubjectType(item.subject_type) }}
              </md-table-cell>
              <md-table-cell md-label="Date/Time">
                {{ toLocal(item.delivered_at || item.created_at,'YYYY-MM-DD HH:mm:ss') }}
              </md-table-cell>
              <md-table-cell md-label="Status">
                <div v-if="item.statusMessage" class="status-message">
                  {{ truncateText(item.statusMessage, 30) }}
                </div><badge :type="getStatusBadge(item.statusCode)">
                {{ formatStatus(item.statusCode) }}
              </badge>
              </md-table-cell>
              <md-table-cell md-label="Balance">
                <span v-if="item.balance !== null && item.balance !== undefined">
                  {{ formatNumber(item.balance) }}
                </span>
                <span v-else class="text-muted">N/A</span>
              </md-table-cell>
              <md-table-cell md-label="Actions">
                <md-button class="md-icon-button md-primary" @click="viewDetails(item)" title="View Details">
                  <md-icon>visibility</md-icon>
                </md-button>
                <md-button
                    v-if="item.statusCode !== '48200'"
                    class="md-icon-button md-success"
                    @click="resendSingle(item)"
                    title="Resend SMS"
                >
                  <md-icon>send</md-icon>
                </md-button>
                <md-button
                    v-if="item.subject && item.subject_type === 'guidance'"
                    class="md-icon-button md-info"
                    @click="viewSubject(item)"
                    title="View Subject"
                >
                  <md-icon>folder</md-icon>
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>

          <div v-else class="empty-state">
            <md-icon>sms</md-icon>
            <p>No SMS logs found</p>
          </div>
        </div>

        <!-- Pagination -->
        <div class="pagination-footer">
          <div>
            <p class="card-category">
              Showing {{ from + 1 }} to {{ to }} of {{ totalItems }} entries
            </p>
          </div>
          <pagination
              class="pagination-no-border pagination-success"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="totalItems"
          />
        </div>
      </md-card-content>
    </md-card>

    <!-- Details Modal -->
    <md-dialog :md-active.sync="modal.details.active" class="details-modal">
      <md-dialog-title>SMS Details</md-dialog-title>
      <md-dialog-content>
        <div class="details-content" v-if="selectedLog">
          <div class="details-section">
            <h5>Message Information</h5>
            <div class="details-grid">
              <div><strong>Recipient:</strong> {{ selectedLog.recipient }}</div>
              <div><strong>Receiver Type:</strong> {{ formatReceiverType(selectedLog.receiver_type) }}</div>
              <div><strong>Receiver:</strong> {{ getReceiverName(selectedLog.receiver) }}</div>
              <div><strong>Sender ID:</strong> {{ selectedLog.sender_id || 'N/A' }}</div>
              <div><strong>Key:</strong> {{ selectedLog.key || 'N/A' }}</div>
            </div>
          </div>

          <div class="details-section">
            <h5>Content</h5>
            <div class="content-full-display">
              {{ selectedLog.content }}
            </div>
          </div>

          <div class="details-section">
            <h5>Subject Information</h5>
            <div class="details-grid">
              <div><strong>Subject Type:</strong> {{ formatSubjectType(selectedLog.subject_type) }}</div>
              <div><strong>Subject ID:</strong> {{ selectedLog.subject_id || 'N/A' }}</div>
              <div><strong>Subject Name:</strong> {{ getSubjectName(selectedLog.subject) }}</div>
            </div>
          </div>

          <div class="details-section">
            <h5>Delivery Information</h5>
            <div class="details-grid">
              <div>
                <strong>Status:</strong>
                <badge :type="getStatusBadge(selectedLog.statusCode)">
                  {{ formatStatus(selectedLog.statusCode) }}
                </badge>
              </div>
              <div><strong>Status Code:</strong> {{ selectedLog.statusCode || 'N/A' }}</div>
              <div><strong>Status Message:</strong> {{ selectedLog.statusMessage || 'N/A' }}</div>
              <div><strong>Delivered At:</strong> {{ toLocal(selectedLog.delivered_at) }}</div>
              <div><strong>Balance:</strong> {{ selectedLog.balance !== null ? formatNumber(selectedLog.balance) : 'N/A' }}</div>
              <div><strong>Error:</strong> {{ selectedLog.error || 'None' }}</div>
            </div>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="closeDetailsModal">Close</md-button>
        <md-button
            v-if="selectedLog && selectedLog.statusCode !== '48200'"
            class="md-success"
            @click="resendFromDetails"
        >
          <md-icon>send</md-icon> Resend SMS
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Resend Confirmation Modal -->
    <md-dialog :md-active.sync="modal.resend.active" class="resend-modal">
      <md-dialog-title>Resend SMS</md-dialog-title>
      <md-dialog-content>
        <div class="modal-content">
          <p>You are about to resend <strong>{{ modal.resend.count || 0 }}</strong> SMS message(s).</p>
          <p>This will attempt to send the same content to the same recipients.</p>

          <md-field>
            <label>Recipient Override (Optional)</label>
            <md-input v-model="modal.resend.recipient_override" placeholder="Enter phone number to override recipient" />
          </md-field>

          <md-field>
            <label>Reason for Resend (Optional)</label>
            <md-textarea v-model="modal.resend.reason" rows="2" placeholder="Why are you resending these messages?" />
          </md-field>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="confirmResend" :disabled="isResending">
          {{ isResending ? 'Sending...' : 'Confirm Resend' }}
        </md-button>
        <md-button class="md-danger" @click="closeResendModal">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Full Content Modal -->
    <md-dialog :md-active.sync="modal.content.active" class="content-modal">
      <md-dialog-title>Full SMS Content</md-dialog-title>
      <md-dialog-content>
        <div class="full-content">
          <div class="content-header">
            <strong>To:</strong> {{ modal.content.recipient }}
            <br>
            <strong>Date:</strong> {{ toLocal(modal.content.datetime) }}
          </div>
          <div class="content-body">
            {{ modal.content.content }}
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="closeContentModal">Close</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DateFormat from '@/mixins/date-format-mixin';
import Badge from '@/components/Badge.vue';
import Pagination from '@/components/Pagination';
import formMixin from '@/mixins/form-mixin';
import _ from 'lodash';

export default {
  name: 'SmsLogList',
  components: {
    Badge,
    Pagination
  },
  mixins: [formMixin, DateFormat],
  data() {
    return {
      logsData: [],
      selectedLogs: [],
      filters: {
        from_date: null,
        to_date: null,
        statusCode: '',
        subject_type: '',
        search: ''
      },
      pagination: {
        currentPage: 1,
        perPage: 15
      },
      perPageOptions: [10, 15, 25, 50, 100],
      isPageLoading: false,
      isResending: false,
      totalItems: 0,
      stats: {
        successful: 0,
        failed: 0,
        pending: 0,
        total: 0,
        balance: 0
      },
      modal: {
        details: {
          active: false
        },
        resend: {
          active: false,
          count: 0,
          recipient_override: '',
          reason: '',
          logs: []
        },
        content: {
          active: false,
          recipient: '',
          content: '',
          datetime: '',
        }
      },
      selectedLog: null
    };
  },
  computed: {
    ...mapGetters('smsLogs', ['meta']),

    logs() {
      return this.logsData || [];
    },

    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    to() {
      var highBound = this.from + this.pagination.perPage;
      if (this.totalItems < highBound) {
        highBound = this.totalItems;
      }
      return highBound;
    }
  },
  created() {
    var today = new Date();
    var startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    this.filters.from_date = this.toLocal(startOfMonth);
    this.filters.to_date = this.toLocal(today);

    this.fetchData();
    this.fetchStats();
  },
  methods: {
    ...mapActions('smsLogs', ['list', 'view', 'getStats', 'resend']),

    async fetchData() {
      try {
        this.isPageLoading = true;
        var params = {
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage
          },
          filter: {}
        };

        if (this.filters.from_date) {
          params.filter.from_date = this.filters.from_date;
        }

        if (this.filters.to_date) {
          params.filter.to_date = this.filters.to_date;
        }

        if (this.filters.statusCode) {
          params.filter.statusCode = this.filters.statusCode;
        }

        if (this.filters.subject_type) {
          params.filter.subject_type = this.filters.subject_type;
        }

        if (this.filters.search) {
          params.filter.search = this.filters.search;
        }

        await this.list(params);
        const response = this.$store.getters['smsLogs/list'] || [];
        this.logsData = response;
        this.totalItems = this.$store.getters['smsLogs/listTotal'] || 0;
      } catch (error) {
        console.error('Error fetching SMS logs:', error);
        this.notifyError('Failed to load SMS logs');
        this.logsData = [];
        this.totalItems = 0;
      } finally {
        this.isPageLoading = false;
      }
    },

    async fetchStats() {
      try {
        var stats = await this.getStats();
        this.stats = stats || {
          successful: 0,
          failed: 0,
          pending: 0,
          total: 0,
          balance: 0
        };
      } catch (error) {
        console.error('Error fetching SMS stats:', error);
      }
    },

    setFilter(field, value) {
      this.filters[field] = this.filters[field] === value ? '' : value;
      this.resetPagination();
      this.fetchData();
    },

    resetPagination() {
      this.pagination.currentPage = 1;
    },

    onFilterChange() {
      this.resetPagination();
      this.fetchData();
    },

    onPerPageChange() {
      this.resetPagination();
      this.fetchData();
    },

    onSearchChange: _.debounce(function() {
      this.resetPagination();
      this.fetchData();
    }, 500),

    async viewDetails(item) {
      try {
        var log = await this.view({ id: item.id, include: 'receiver,subject' });
        this.selectedLog = log;
        this.modal.details.active = true;
      } catch (error) {
        console.error('Error fetching SMS details:', error);
        this.notifyError('Failed to load SMS details');
      }
    },

    closeDetailsModal() {
      this.modal.details.active = false;
      this.selectedLog = null;
    },

    resendSingle(log) {
      if (this.modal.details.active) {
        this.modal.details.active = false;
      }
      this.selectedLogs = [log];
      this.showResendModal();
    },

    resendSelected() {
      if (!this.selectedLogs || this.selectedLogs.length === 0) {
        this.notifyWarning('Please select at least one SMS to resend');
        return;
      }

      if (this.modal.details.active) {
        this.modal.details.active = false;
      }

      this.showResendModal();
    },

    resendFromDetails() {
      if (this.selectedLog) {
        this.modal.details.active = false;
        this.resendSingle(this.selectedLog);
      }
    },

    showResendModal() {
      this.modal.resend.count = this.selectedLogs ? this.selectedLogs.length : 0;
      this.modal.resend.logs = this.selectedLogs || [];
      this.modal.resend.recipient_override = '';
      this.modal.resend.reason = '';
      this.modal.resend.active = true;
    },

    closeResendModal() {
      this.modal.resend.active = false;
      this.selectedLogs = [];
    },

    async confirmResend() {
      this.isResending = true;

      try {
        var ids = this.selectedLogs.map(function(item) {
          return item.id;
        });

        var payload = {
          sms_ids: ids,
          recipient_override: this.modal.resend.recipient_override || null,
          reason: this.modal.resend.reason || null
        };

        // const result = await this.resend(payload);
        this.notifyWarning('Coming soon. messages failed to send');
        // this.notifySuccess('SMS messages resent successfully');
        // this.closeResendModal();
        //
        // await this.fetchData();
        // await this.fetchStats();
        //
        // if (result && result.success) {
        //   this.notifyInfo(result.success + ' messages sent successfully');
        // }
        // if (result && result.failed) {
        //   this.notifyWarning(result.failed + ' messages failed to send');
        // }
      } catch (error) {
        console.error('Error resending SMS:', error);
        this.notifyError('Failed to resend SMS messages');
      } finally {
        this.isResending = false;
      }
    },

    clearSelection() {
      this.selectedLogs = [];
    },

    showFullContent(log) {
      this.modal.content.active = true;
      this.modal.content.recipient = log.recipient;
      this.modal.content.content = log.content;
      this.modal.content.datetime = log.created_at;
    },

    closeContentModal() {
      this.modal.content.active = false;
    },

    viewSubject(log) {
      if (log.subject && log.subject_type === 'guidance') {
        if (this.modal.details.active) {
          this.modal.details.active = false;
        }
        this.$router.push({
          name: 'View Guidance',
          params: { id: log.subject_id }
        });
      }
    },

    getReceiverName(receiver) {
      if (!receiver) return 'Unknown';
      if (receiver.first_name || receiver.last_name) {
        var firstName = receiver.first_name || '';
        var lastName = receiver.last_name || '';
        var middleName = receiver.middle_name || '';
        return [firstName, middleName, lastName].filter(Boolean).join(' ') || receiver.name || 'Unknown';
      }
      return receiver.name || receiver.recipient || 'Unknown';
    },

    getReceiverTypeClass(type) {
      var classes = {
        'labourer': 'badge-labourer',
        'supervisor': 'badge-supervisor',
        'manager': 'badge-manager',
        'user': 'badge-user'
      };
      return classes[type] || 'badge-default';
    },

    formatReceiverType(type) {
      var types = {
        'labourer': 'Labourer',
        'supervisor': 'Supervisor',
        'manager': 'Manager',
        'user': 'User'
      };
      return types[type] || type || 'Unknown';
    },

    getSubjectTypeClass(type) {
      var classes = {
        'guidance': 'badge-guidance',
        'schedule': 'badge-schedule',
        'labourer': 'badge-labourer',
        'takeon': 'badge-takeon'
      };
      return classes[type] || 'badge-default';
    },

    formatSubjectType(type) {
      var types = {
        'guidance': 'Guidance',
        'schedule': 'Schedule',
        'labourer': 'Labourer',
        'takeon': 'Takeon'
      };
      return types[type] || type || 'Unknown';
    },

    getSubjectName(subject) {
      if (!subject) return 'N/A';
      return subject.name || subject.subject || subject.title || subject.id || 'N/A';
    },

    formatStatus(code) {
      if (code === '48200') return 'Successful';
      if (code === '0') return 'Failed';
      if (code === 'pending') return 'Pending';
      return code || 'Unknown';
    },

    getStatusBadge(code) {
      if (code === '48200') return 'success';
      if (code === '0') return 'danger';
      if (code === 'pending') return 'warning';
      return 'info';
    },

    truncateText(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },

    formatNumber(number) {
      if (number === null || number === undefined) return '0';
      return new Intl.NumberFormat().format(number);
    }
  },

  watch: {
    'filters.from_date'() {
      this.resetPagination();
      this.fetchData();
    },
    'filters.to_date'() {
      this.resetPagination();
      this.fetchData();
    },
    'filters.statusCode'() {
      this.resetPagination();
      this.fetchData();
    },
    'filters.subject_type'() {
      this.resetPagination();
      this.fetchData();
    },
    pagination: {
      handler: 'fetchData',
      deep: true
    }
  }
};
</script>

<style scoped>
.sms-log-page {
  padding: 20px;
}

.stats-cards {
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-card.success { border-left: 4px solid #4caf50; }
.stat-card.danger { border-left: 4px solid #f44336; }
.stat-card.warning { border-left: 4px solid #ff9800; }
.stat-card.info { border-left: 4px solid #00bcd4; }
.stat-card.primary { border-left: 4px solid #2196f3; }

.stat-value {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

.filters-bar {
  margin-bottom: 24px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 8px;
}

.bulk-actions {
  background: #e3f2fd;
  padding: 12px 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.selected-count {
  font-weight: 500;
  color: #1976d2;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.sms-log-table td {
  vertical-align: middle;
}

.content-preview {
  max-width: 250px;
  word-break: break-word;
}

.content-full {
  margin-top: 4px;
}

.content-full a {
  color: #2196f3;
  text-decoration: none;
}

.receiver-info {
  font-size: 11px;
  color: #666;
  display: block;
  margin-top: 2px;
}

.receiver-type-badge {
  display: inline-block;
  padding: 0 6px;
  border-radius: 3px;
  font-size: 8px;
  font-weight: bold;
  text-transform: uppercase;
  margin-right: 4px;
}


.subject-badge {
  display: inline-block;
  padding: 0 6px;
  border-radius: 3px;
  font-size: 8px;
  font-weight: bold;
  text-transform: uppercase;
  margin-right: 4px;
}


.subject-name {
  font-size: 10px;
  color: #666;
}

.text-muted {
  color: #999;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state md-icon {
  font-size: 64px;
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
}

.pagination-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
}
.md-dialog{

  z-index: 10000;
}
.details-modal .md-dialog,
.resend-modal .md-dialog,
.content-modal .md-dialog {
  max-width: 700px;
  width: 90%;
}

.details-content {
  max-height: 500px;
  overflow-y: auto;
}

.details-section {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;
}

.details-section h5 {
  margin: 0 0 10px 0;
  color: #9c27b0;
  font-size: 13px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.details-grid div {
  font-size: 12px;
}

.content-full-display {
  background: #f9f9f9;
  padding: 15px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  border: 1px solid #e0e0e0;
  max-height: 200px;
  overflow-y: auto;
}

.full-content {
  padding: 10px 0;
}

.content-header {
  background: #f5f5f5;
  padding: 10px 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 12px;
}

.content-body {
  background: #fafafa;
  padding: 15px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.6;
  border: 1px solid #e0e0e0;
  min-height: 100px;
}

.modal-content {
  padding: 10px 0;
}

.modal-content ul {
  margin: 10px 0 15px 20px;
}

@media (max-width: 768px) {
  .sms-log-page {
    padding: 10px;
  }

  .filters-bar {
    flex-direction: column;
    gap: 10px;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .stat-card {
    padding: 10px;
  }

  .stat-value {
    font-size: 20px;
  }
}
</style>